import { AccountUtmParams, GeoAutocompleteAddress, StopSituation } from '@brenger/api-client';
import { AnyAction } from '@reduxjs/toolkit';
import { LOCATION_CHANGE, LocationChangeAction } from 'connected-react-router';
import { r } from '../../../routes';
import { AppListenerApi, startAppListening } from '../../../state/middlewares/listener';
import { StopType } from '../../../types';
import * as appUtils from '../../../utils/basics';
import { getAccountHasBusinessFlow } from '../../User/ducks';
import { ExpectedUtmParams } from '.././interface';
import { AuctionType } from '../components/SubStepAuctionTypes';
import { HomeSituationType } from '../components/SubStepHomeSituation';
import { actions } from '../ducks';
import { proceed } from '../proceed';
import { PsPrefillActionType, PsPrefillOtherAction, PSPrefillProductAction } from '../typings';

/** A referrer that is not an absolute url (empty, relative) has no pathname to check. */
const parseAbsoluteUrl = (url: string): URL | null => {
  try {
    return new URL(url);
  } catch (_) {
    return null;
  }
};

/**
 * URLSearchParams already decoded the value; decoding again only matters for a double-encoded sender. A value with a
 * literal % (e.g. "100% wol") is not valid percent-encoding, so it is kept as is instead of throwing.
 */
const decodeAgain = (value: string, decode: (encoded: string) => string): string => {
  try {
    return decode(value);
  } catch (_) {
    return value;
  }
};

/**
 * Reads the widget / landing page parameters (addresses, UTMs, AB tests, referrer, ps prefill, pickup type) on the
 * first landing in the flow and proceeds to the step they lead to (same behavior as the former readWidgetParameters saga).
 */
export async function readWidgetParameters(action: LocationChangeAction, api: AppListenerApi): Promise<void> {
  const isGeneralFlow = action.payload.location.pathname.includes(r.generalFlow.index());
  if (!isGeneralFlow || !action.payload.isFirstRendering || window.location.search.length === 0) {
    return;
  }
  // First check if reset and force GA ID is needed
  try {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('reset_flow') === 'full') {
      /**
       * Coming from the widget we only want to reset the flow
       * but keep destination details around.
       */
      api.dispatch(actions.resetFlow(false));
    }
    const forceGa = searchParams.get('force_ga');
    if (forceGa) {
      sessionStorage.setItem('brenger_tr_flow_force_ga', forceGa);
    }
  } catch (error) {
    appUtils.logError(error);
    appUtils.logException('[GFS] Failed to execute reset forms', { extra: { action, error } });
  }
  // This is copied from the widget project, might not be needed.
  // FIXME: Should be removed when GEO is harmonised
  const widgetAddressWhiteList: Array<keyof GeoAutocompleteAddress> = [
    'administrative_area',
    'country_code',
    'country_name',
    'latitude',
    'line1',
    'line2',
    'locality',
    'longitude',
    'municipality',
    'postal_code',
    'secondary_subdivision',
  ];
  // then read new details
  try {
    const searchParams = new URLSearchParams(window.location.search);
    const pickup = {};
    widgetAddressWhiteList.forEach(key => {
      const keyName = `pickup[${key}]`;
      if (!searchParams.has(keyName)) {
        return;
      }
      pickup[key] = searchParams.get(keyName);
    });
    const delivery = {};
    widgetAddressWhiteList.forEach(key => {
      const keyName = `delivery[${key}]`;
      if (!searchParams.has(keyName)) {
        return;
      }
      delivery[key] = searchParams.get(keyName);
    });
    /**
     * First set the UTM details
     */
    const utm: { [key in ExpectedUtmParams]?: string } = {};
    searchParams.forEach((value, key) => {
      // Every UTM parameter gets saved in the utm object
      if (key.includes('utm')) {
        utm[key] = value;
      }
    });
    // Only update if we have UTM parameters saved to the object,
    // Important, if none exist we don't want to clear the previous ones
    if (Object.keys(utm).length) {
      api.dispatch(actions.setUtm(utm));
    }
    /**
     * Then collect rest of read actions
     */
    const readActions: AnyAction[] = [];
    // Save AB test params
    try {
      const ab = searchParams.get('wpab');
      if (ab) {
        const tests = JSON.parse(decodeURIComponent(ab)).map(test => ({
          id: Number(test[0]),
          v: Number(test[1]),
        }));
        readActions.push(actions.setAb(tests));
      } else {
        readActions.push(actions.resetAb());
      }
    } catch (_) {
      readActions.push(actions.resetAb());
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - quick fix to make the checks work
    if (pickup.latitude && pickup.longitude && pickup.locality) {
      readActions.push(actions.setDestinationDetails(pickup as GeoAutocompleteAddress, StopType.PICKUP));
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - quick fix to make the checks work
    if (delivery.latitude && delivery.longitude && delivery.locality) {
      readActions.push(actions.setDestinationDetails(delivery as GeoAutocompleteAddress, StopType.DELIVERY));
    }

    // Is there a Pickup UTM? else set to NULL
    const accountPickup = searchParams.get(AccountUtmParams.PICKUP);
    readActions.push(
      actions.setDestinationMeta(accountPickup ? { utm_account: accountPickup } : null, StopType.PICKUP)
    );
    // Is there a Delivery UTM? else set to NULL
    const accountDelivery = searchParams.get(AccountUtmParams.DELIVERY);
    readActions.push(
      actions.setDestinationMeta(accountDelivery ? { utm_account: accountDelivery } : null, StopType.DELIVERY)
    );
    const referrer = searchParams.get(`meta[referrer]`);
    let isMpUser = false;
    if (referrer !== null) {
      const decodedReferrer = decodeAgain(referrer, decodeURI);
      api.dispatch(actions.setReferrer(decodedReferrer));
      // if MP user, we skip the first two steps
      const referrerUrl = parseAbsoluteUrl(decodedReferrer);
      isMpUser =
        // Check if the referrer comes from the MP landing page
        !!referrerUrl?.pathname.includes('/marktplaats') &&
        // Check if the current page has a MP UTM source, to separate MP users from other advertisement traffic
        window.location.search.includes('utm_source=marktplaats');
      // We have our own way of setting a prefill, let's check if we have any
      let prefillAction: PsPrefillOtherAction | PSPrefillProductAction | null = null;
      const psPrefillAction = searchParams.get('ps-action') as PsPrefillActionType | null;
      const psPrefillValue = decodeAgain(searchParams.get('ps-value') || '', decodeURIComponent);
      const psWarn = searchParams.get('ps-should-warn') === 'true';
      if (psPrefillAction && psPrefillValue) {
        // We have a PREFILL action
        if (psPrefillAction === 'PREFILL') {
          prefillAction = {
            type: psPrefillAction,
            value: psPrefillValue,
            shouldWarn: psWarn ?? true,
          };
        }
        // We have a CONFIG or SEARCH
        if (psPrefillAction === 'CONFIG' || psPrefillAction === 'SEARCH') {
          prefillAction = {
            type: psPrefillAction,
            value: psPrefillValue,
          };
        }
      }
      // We dont care about the outcome:
      // We either have an action or we set to null (to prevent cache side effects)
      readActions.push(actions.setPrefillPs(prefillAction));
    }
    readActions.forEach(readAction => api.dispatch(readAction));
    let pathnameOverwrite: string | null = null;
    if (isMpUser) {
      /**
       * Show a loader so we can navigate and flag routing affected
       */
      api.dispatch(actions.setStepLoading(true));
      api.dispatch(actions.setChoice({ situation: 'home' }));
      api.dispatch(actions.setChoice({ home_situation: 'marktplaats' }));
      api.dispatch(actions.showMarktplaatsWelcomeModal(true));
      pathnameOverwrite = r.generalFlow.homeSituation.index();
    } else {
      const pickupType = searchParams.get('pickup-type') as StopSituation;
      const pickupTypeSpecific = searchParams.get('pickup-type-specific');
      if (pickupType) {
        /**
         * Show a loader so we can navigate and flag routing is affected
         */
        api.dispatch(actions.setStepLoading(true));
        api.dispatch(actions.setChoice({ situation: pickupType }));
        pathnameOverwrite = r.generalFlow.pickup.index();
        if (pickupTypeSpecific) {
          // pickupTypeSpecific can either indicate auction type or home situation
          if (pickupType === 'auction') {
            api.dispatch(actions.setChoice({ auction_type: pickupTypeSpecific as AuctionType }));
            pathnameOverwrite = r.generalFlow.pickup.auction();
          } else {
            api.dispatch(actions.setChoice({ home_situation: pickupTypeSpecific as HomeSituationType }));
            pathnameOverwrite = r.generalFlow.homeSituation.index();
          }
        }
      }
    }
    /**
     * When entering the flow and the user is a former BF user.
     * We pre-select store and the user can start at items step.
     */
    const isBusinessFlow = getAccountHasBusinessFlow(api.getState());
    if (isBusinessFlow) {
      pathnameOverwrite = r.generalFlow.destination.index();
    }
    /**
     * Above actions could lead to input changes, and the user could skip certain steps in the flow because of it.
     * Besides setting values it sets pathnameOverwrite, the pathname is the exact same as the page were you manually fill the form.
     * This way we can use the "getProceedAction", to determine what is the next path.
     */
    if (pathnameOverwrite) {
      await proceed({ pathnameOverwrite });
    }
    api.dispatch(actions.setStepLoading(false));
  } catch (e) {
    appUtils.logError(e);
    // if above statements crash for whatever reason, we should not be stuck in loading state
    api.dispatch(actions.setStepLoading(false));
  }
}

/**
 * Registers the widget parameter read on every navigation (same behavior as the former destinationSaga watcher).
 */
export const startWidgetParamsListeners = (): void => {
  startAppListening({
    type: LOCATION_CHANGE,
    effect: (action, api) => readWidgetParameters(action as LocationChangeAction, api),
  });
};
